import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, UserRole, UserTier } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

export interface UseCurrentRoleResult {
  role: UserRole | null;
  tier: UserTier | null;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/**
 * The current socket's platform role/tier, for the admin section's
 * client-side gating (`RoleGate.tsx`) and staff-only affordances. Reuses
 * the FROZEN `settings.getProfile` op (F10, `apps/web/src/routes/settings`)
 * — `SettingsProfileResult extends userPublicSchema`, which carries `.role`
 * — rather than inventing a second "who am I" op. `AuthContextValue`
 * (`apps/web/src/auth/AuthProvider.tsx`) only holds `userId`/`accessToken`,
 * not `role`, so this is the one place `@xdocs/web` learns the caller's
 * role.
 *
 * `queryKey` follows the `['user', userId, …]` convention
 * `useCreditsBalance.ts` established so a live `user:{userId}` push
 * (`ws/queryInvalidation.ts` invalidates the `['user', userId]` prefix —
 * e.g. after an admin's OWN `user.role_change`, or self-demotion) refetches
 * this instead of serving a stale role for the rest of the session.
 */
export function useCurrentRole(): UseCurrentRoleResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected' && userId !== null;

  const result = useQuery<{ role: UserRole; tier: UserTier }, ErrorAlert>({
    queryKey: ['user', userId, 'profile', 'role'],
    queryFn: async () => {
      const profile = await requestTyped(wsClient, 'settings.getProfile', undefined);
      return { role: profile.role, tier: profile.tier };
    },
    enabled,
  });

  return {
    role: result.data?.role ?? null,
    tier: result.data?.tier ?? null,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error ?? null,
    refetch: () => void result.refetch(),
  };
}
